import React from 'react';
import cn from 'classnames';
import { SelectOption } from '@app/types';
import styles from './PublicShareStatus.module.scss';
import ResourcePermissionsEditor from '../ResourcePermissions';
import { ShareStatus } from '@models/ShareStatus';
import { SharedResourceType } from '@reducers/data/shareStatuses';
import SelectMenu from '@components/Menus/SelectMenu';
import { ChevronDownIcon } from '@components/Icons';

const publicShareOptions = [
  { label: 'Only people invited can view', value: 'private' },
  { label: 'Anyone with the link can view', value: 'public' },
];

const ShareStatusIcon = ({ className, children }) => (
  <svg className={cn(styles.icon, className)} viewBox="0 0 24 24">
    <g strokeWidth="1.17" strokeLinejoin="round">
      {children}
    </g>
  </svg>
);

const publicShareStatusToShareOption = (publiclyShared: boolean): SelectOption => {
  if (publiclyShared) {
    return publicShareOptions[1];
  }
  return publicShareOptions[0];
};

export default function PublicShareStatus<P>({
  sharedResourceType,
  shareStatus,
  sharePublicly,
  unsharePublicly,
  addPublicPermission,
  removePublicPermission,
}: {
  sharedResourceType: SharedResourceType;
  shareStatus: ShareStatus<P>;
  sharePublicly: () => void;
  unsharePublicly: () => void;
  addPublicPermission: (permission: P) => void;
  removePublicPermission: (permission: P) => void;
}) {
  const onPublicAccessChange = (value: 'public' | 'private') => {
    if (value == 'public') {
      sharePublicly();
    } else if (value == 'private') {
      unsharePublicly();
    }
  };

  return (
    <>
      <div
        className={cn(styles.publicSharingIndicator, shareStatus.publiclyShared && styles.isPublic)}
      >
        <ShareStatusIcon className={styles.locked}>
          <circle cx="12" cy="13.5" r="0.9" stroke="none" />
          <path fill="none" d="M9.4 10.5 C 9.6 6.7 14.4 6.7 14.6 10.5" />
          <rect x="8.5" y="10.5" width="7" height="5.8" rx="1.5" fill="none" />
        </ShareStatusIcon>
        <ShareStatusIcon className={styles.global}>
          <circle cx="12" cy="12" r="5.85" fill="none" />
          <path
            d="M6.1 12H17.8 M12 6.2C13.5 7.8 14.3 9.8 14.3 12C14.3 14.2 13.5 16.2 12 17.8C10.5 16.2 9.7 14.2 9.7 12C9.7 9.8 10.5 7.8 12 6.2V6.2Z"
            fill="none"
          />
        </ShareStatusIcon>
      </div>
      <SelectMenu
        options={publicShareOptions}
        value={publicShareStatusToShareOption(shareStatus.publiclyShared).value}
        onChange={onPublicAccessChange}
        menuClassName={styles.publicShareMenu}
        triggerClassName={styles.publicShareMenuTrigger}
        expandFrom="top-left"
        triggerProps={{
          children: (
            <>
              {publicShareStatusToShareOption(shareStatus.publiclyShared).label}{' '}
              <ChevronDownIcon color="textSecondary" />
            </>
          ),
        }}
      />
      <ResourcePermissionsEditor
        globalPermissions
        sharedResourceType={sharedResourceType}
        permissions={shareStatus.publicPermissionsMap}
        addPermission={addPublicPermission}
        removePermission={removePublicPermission}
        disabled={!shareStatus.publiclyShared}
      />
    </>
  );
}
